import { BasePickerProps } from "@mui/x-date-pickers/internals";
import { count } from "console";
import yup from "../../../../shared/validation/yupExtensions";
import { FilteredDropDownModel, ListModel, NameTranslationUDF, UnitTranslationUDF } from "shared/interfaces/core-interfaces";

export interface ReferenceDataSliceModel {
    statusCode: string,
    isLoading: boolean,
    isModalLoading: boolean,
    message: string,
    showNotification: boolean,
    listOfEmployeePositions: EmployeePositionModel[] | [],
    employeePosition: EmployeePositionModel | null,
    listOfParticipantRoles: ParticipantRoleModel[] | [],
    participantRole: ParticipantRoleModel | null,
    listOfUnits: UnitModel[] | [],
    unit: UnitModel | null,
    listOfSources: SourceModel[] | [],
    source: SourceModel | null,
    listOfAccountingNumbers: AccountingNumberModel[] | [],
    accountingNumber: AccountingNumberModel | null,
    listOfCateogires: CategoryModel[] | [],
    category: CategoryModel | null,
    listOfPaymentMethods: PaymentMethodModel[] | [],
    listOfIndustrySectors: SimpleReferenceModel[] | [],
    listOfEducationTypes: SimpleReferenceModel[] | [],
    listOfDealPipelines: SimpleReferenceModel[] | [],
    listOfPartnerTypes: PartnerTypeModel[] | [],
    listOfAbsenceTypes: AbsenceTypeModel[] | [],
    absenceType: AbsenceTypeModel | null,
    partnerType: PartnerTypeModel | null,
    industrySector: SimpleReferenceModel | null,
    educationType: SimpleReferenceModel | null,
    dealPipeline: SimpleReferenceModel | null,
    paymentMethod: PaymentMethodModel | null,
    listOfActiveArticles: ArticleModel[] | [],
    listOfInactiveArticles: ArticleModel[] | [],
    article: ArticleModel | null,
    listOfActiveTravelAllowances: TravelAllowance[] | [],
    listOfInactiveTravelAllowances: TravelAllowance[] | [],
    travelAllowance: TravelAllowance | null,
    travelAllowanceCreateData: TravelAllowanceCreateData | null,
    listOfActiveDriverTravelAllowances: DriverTravelAllowance[] | [],
    listOfInactiveDriverTravelAllowances: DriverTravelAllowance[] | [],
    driverTravelAllowance: DriverTravelAllowance | null,
    driverTravelAllowanceCreateData: DriverTravelAllowanceCreateData | null,
    createData: {
        countries: ListModel<string>[] | [],
        sourceTypes: ListModel<number>[] | [],
        filteredContactsList: FilteredDropDownModel,
        filteredPartnersList: FilteredDropDownModel,
        projects: ListModel<string>[] | [],
        articleTypes: ListModel<number>[] | [],
    }
}

//#region Education type
export interface GetEducationTypesResponse {
    educationTypes: SimpleReferenceModel[]
}
//#endregion

//#region Deal pipeline
export interface GetDealsPipelineTypesResponse {
    dealsPipelineTypes: SimpleReferenceModel[]
}
//#endregion

//#region Shared simple refernece data
export interface SimpleReferenceModel {
    id: string | null,
    name: string,
    description?: string | null,
    defaultLanguage?: string | null,
    translations?: NameTranslationUDF[] | null,
    disableAction?: boolean,
    isActive: boolean | null
}

export const initSimpleReferenceModel: SimpleReferenceModel = {
    id: null,
    name: '',
    description: '',
    defaultLanguage: null,
    translations: [],
    isActive: null
}

export const initAbsenceTypeRequest: AbsenceTypeRequest = {
    id: null,
    name: '',
    description: '',
    defaultLanguage: null,
    translations: [],
    isActive: null,
    variant: 1,
    attachmentRequired: false,
    isPaid: false,
    isReadOnly: false
}

export const TranslationUDFScheme = yup.object().shape({
    language: yup.string().defined(),
    translationName: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    translationDescription: yup.string().max(500, "Description must be at most 500 characters.").defined(),
})

export const SimpleReferenceModelScheme = yup.object().shape({
    id: yup.string().defined().nullable(),
    name: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    description: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(TranslationUDFScheme).nullable(),
    isActive: yup.boolean().defined().nullable()
})

export const AbsenceTypeRequestModelScheme = yup.object().shape({
    id: yup.string().defined().nullable(),
    name: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    description: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(TranslationUDFScheme).nullable(),
    isActive: yup.boolean().defined().nullable(),
    attachmentRequired: yup.boolean().defined(),
    isPaid: yup.boolean().defined(),
    variant: yup.number().defined(),
    isReadOnly: yup.boolean().defined()
})
//#endregion


//#region Unit
export interface UnitModel {
    id?: number | null,
    name: string,
    fullName?: string | null,
    description?: string | null,
    defaultLanguage?: string | null,
    translations?: UnitTranslationUDF[] | null,
    isActive: boolean | null
}

export const initUnitModel: UnitModel = {
    id: null,
    name: '',
    description: '',
    fullName: '',
    defaultLanguage: null,
    translations: [],
    isActive: null
}

export const UnitTranslationUDFScheme = yup.object().shape({
    language: yup.string().defined(),
    translationName: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    translationDescription: yup.string().max(500, "Description must be at most 500 characters."),
    translationFullName: yup.string().max(250, "Name must be at most 250 characters."),
})

export const UnitModelScheme = yup.object().shape({
    id: yup.number().nullable(),
    name: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    fullName: yup.string().max(250, "Name must be at most 250 characters.").nullable(),
    description: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(UnitTranslationUDFScheme).nullable(),
    isActive: yup.boolean().defined().nullable()
})

//#endregion

//#region Employee position
export interface EmployeePositionModel {
    id: string | null,
    name: string,
    description?: string | null,
    defaultLanguage?: string | null,
    translations?: NameTranslationUDF[] | null,
    isActive: boolean | null
}

export const initEmployeePositionModel: EmployeePositionModel = {
    id: null,
    name: '',
    description: '',
    defaultLanguage: null,
    translations: [],
    isActive: null
}

export const EmployeePositionModelScheme = yup.object().shape({
    id: yup.string().defined().nullable(),
    name: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    description: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(TranslationUDFScheme).nullable(),
    isActive: yup.boolean().defined().nullable()
})

//#endregion

//#region Source
export interface SourceModel {
    id: string | null,
    name: string,
    description?: string | null,
    defaultLanguage?: string | null,
    translations?: NameTranslationUDF[] | null,
    isActive: boolean | null,
    selectedSourceTypes: ListModel<number>[] | [],
}

export const initSourceModel: SourceModel = {
    id: null,
    name: '',
    description: '',
    defaultLanguage: null,
    translations: [],
    isActive: null,
    selectedSourceTypes: []
}

export const SourceModelScheme = yup.object().shape({
    id: yup.string().defined().nullable(),
    name: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    description: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(TranslationUDFScheme).nullable(),
    isActive: yup.boolean().defined().nullable(),
    selectedSourceTypes: yup.array().min(1, 'At least one type is required.').required("Article type is required.").defined(),
})

//#endregion

//#region Accounting Number

export interface AccountingNumberModel {
    id?: string | null,
    isActive: boolean | null
    internationalNumber: number,
    internationalName: string,
    internationalDescription?: string | null,
    defaultLanguage?: string | null,
    accountingNumberTypeId?: number | null,
    translations?: NameTranslationUDF[] | null,
    countryIdsForFront?: string[] | null;
    countryAccountingNumber_UDFs?: CountryAccountingNumberUDFRequest[] | null
}

export interface CountryAccountingNumberUDFRequest {
    countryId: string,
    number: string
}

export const initAccountingNumberModel: AccountingNumberModel = {
    id: null,
    defaultLanguage: null,
    translations: [],
    internationalNumber: 0,
    internationalName: "",
    accountingNumberTypeId: null,
    isActive: true,
    countryIdsForFront: []
}

export const AccountingNumberModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    isActive: yup.boolean().defined().nullable(),
    internationalNumber: yup.number().transform((value) => Number.isNaN(value) ? undefined : value).required("International number is required.").typeError("International number must be a valid number."),
    internationalName: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    internationalDescription: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    accountingNumberTypeId: yup
        .number()
        .nullable() // dozvoljava null da bi dropdown mogao da se clear-uje
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .test(
            "Type required",
            "Type is required.",
            (value) => value !== null && value !== undefined
        ),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(TranslationUDFScheme).nullable(),
    countryIdsForFront: yup.array().nullable(),
    countryAccountingNumber_UDFs: yup.array().nullable()
})

//#endregion

//#region Cateogry

export interface CategoryModel {
    id: string | null,
    name: string,
    description?: string | null,
    defaultLanguage?: string | null,
    translations?: NameTranslationUDF[] | null,
    isActive: boolean | null
}

export const initCategoryModel: CategoryModel = {
    id: null,
    name: '',
    description: '',
    defaultLanguage: null,
    translations: [],
    isActive: null
}

export const CategoryModelScheme = yup.object().shape({
    id: yup.string().defined().nullable(),
    name: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    description: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(TranslationUDFScheme).nullable(),
    isActive: yup.boolean().defined().nullable()
})

//#endregion

//#region Payment method

export interface PaymentMethodModel {
    id: string | null,
    name: string,
    description?: string | null,
    defaultLanguage?: string | null,
    translations?: NameTranslationUDF[] | null,
    isActive: boolean | null,
    isReadOnly?: boolean;
}

export const initPaymentMethodModel: PaymentMethodModel = {
    id: null,
    name: '',
    description: '',
    defaultLanguage: null,
    translations: [],
    isActive: null,

}

export const PaymentMethodModelScheme = yup.object().shape({
    id: yup.string().defined().nullable(),
    name: yup.string().max(250, "Name must be at most 250 characters.").required("Name is required."),
    description: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(TranslationUDFScheme).nullable(),
    isActive: yup.boolean().defined().nullable()
})

//#endregion

//#region Partner Type
export interface PartnerTypeModel extends SimpleReferenceModel {
    isReadOnly?: boolean;
}

//#endregion

//#region Absence type

export interface AbsenceTypeModel extends SimpleReferenceModel {
    isReadOnly?: boolean;
    attachmentRequired?: boolean;
    isPaid?: boolean;
    consumesVacationDays?: boolean;
    consumesOvertime?: boolean;
    variant?: AbsenceTypeVariants;
}

export enum AbsenceTypeVariants {
    Days = 1,
    Hours = 2,
    Both = 3
}

export interface AbsenceTypeRequest extends SimpleReferenceModel {
    variant: number,
    attachmentRequired: boolean,
    isPaid: boolean,
    isReadOnly: boolean
}



//#endregion

//#region Articles

export interface ArticleModel extends SimpleReferenceModel {
    number: string | ''
    selectedPartners: ListModel<string>[] | [],
    selectedContacts: ListModel<string>[] | [],
    selectedProjects: ListModel<string>[] | [],
    selectedArticleTypes: ListModel<number>[] | [],
}

export const initArticleModel: ArticleModel = {
    number: "",
    selectedPartners: [],
    selectedContacts: [],
    selectedProjects: [],
    selectedArticleTypes: [],
    id: null,
    name: "",
    isActive: null
}

export const ArticleModelScheme = SimpleReferenceModelScheme.shape({
    number: yup.string().required("Article number is required."),
    selectedPartners: yup.array().defined(),
    selectedContacts: yup.array().defined(),
    selectedProjects: yup.array().defined(),
    selectedArticleTypes: yup.array().min(1, 'At least one article type must be selected.').required("Article type is required.").defined(),
})

export interface ArticleCreateData {
    projects: ListModel<string>[] | [],
    articleTypes: ListModel<number>[] | [],
}

//#endregion

//#region Participant Role
export interface ParticipantRoleModel {
    id: string | null,
    name: string,
    description?: string | null,
    defaultLanguage?: string | null,
    translations?: NameTranslationUDF[] | null,
    isActive: boolean | null
}

export const initParticipantRoleModel: ParticipantRoleModel = {
    id: null,
    name: '',
    description: '',
    defaultLanguage: null,
    translations: [],
    isActive: null
}

export const ParticipantRoleModelScheme = yup.object().shape({
    id: yup.string().defined().nullable(),
    name: yup.string().max(250, "Name must be at most 250 characters.").required("Participant role is required."),
    description: yup.string().max(500, "Description must be at most 500 characters.").nullable(),
    defaultLanguage: yup.string().nullable(),
    translations: yup.array().of(TranslationUDFScheme).nullable(),
    isActive: yup.boolean().defined().nullable()
})

//#endregion

export interface DriverTravelAllowance {
    id?: string,
    isInland: true,
    vehicleDescription: string,
    vehicleType: number,
    paymentType: number,
    amount: number,
    currency: string,
    additionalPassengerAmount: number,
    additionalPassengerCurrency: string,
    isActive?: boolean,
}

export interface DriverTravelAllowanceList {
    driverTravelAllowances: DriverTravelAllowance[];
}

export interface DriverTravelAllowanceCreateData {
    paymentTypes: ListModel<string>[];
    vehicleTypes: ListModel<string>[];
    currencies: ListModel<string>[];
}


export interface TravelAllowance {
    id?: string | null,
    isInland: boolean,
    amount: number,
    currency: string,
    countryId: number,
    country?: string,
    dayTypeOrDuration: string,
    defaultLanguage: string,
    isActive?: boolean,
    dayTypeOrDurationTranslations?: {
        language: string,
        translationName: string,
        translationDescription: string
    }[] | null
}

export interface TravelAllowanceList {
    travelAllowences: TravelAllowance[];
}

export interface TravelAllowanceCreateData {
    countries: ListModel<number>[];
    currencies: ListModel<string>[];
}

export const initTravelAllowanceModel: TravelAllowance = {
    isInland: true,
    amount: '' as unknown as number,
    currency: '',
    countryId: 0,
    country: '',
    dayTypeOrDuration: '',
    defaultLanguage: '',
    dayTypeOrDurationTranslations: []
}

// Same shape as TranslationUDFScheme, but the name field is labelled
// "Day type or duration" for travel allowances.
export const TravelAllowanceTranslationUDFScheme = yup.object().shape({
    language: yup.string().defined(),
    translationName: yup.string().max(250, "Day type or duration must be at most 250 characters.").required("Day type or duration is required."),
    translationDescription: yup.string().max(500, "Description must be at most 500 characters.").defined(),
})

export const TravelAllowanceModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    isInland: yup.boolean().defined(), 
    amount: yup.number().transform((value) => Number.isNaN(value) ? undefined : value).required("Amount is required.").typeError("Amount must be a valid number."), 
    currency: yup.string().required("Currency is required."), 
    countryId: yup.number().transform((value) => Number.isNaN(value) ? undefined : value).min(1, "Country is required.").required("Country is required.").typeError("Country must be a valid number."),
    country: yup.string(),
    // dayTypeOrDuration is no longer entered directly; it is mirrored from the default-language
    // translation at submit time. The default name is still enforced via the translations array below.
    dayTypeOrDuration: yup.string(),
    defaultLanguage: yup.string().required("Default language is required."),
    dayTypeOrDurationTranslations: yup.array().of(TravelAllowanceTranslationUDFScheme).nullable(),
})

export const initDriverTravelAllowanceModel: DriverTravelAllowance = {
    isInland: true,
    vehicleDescription: '',
    vehicleType: 0,
    paymentType: 0,
    amount: '' as unknown as number,
    currency: '',
    additionalPassengerAmount: '' as unknown as number,
    additionalPassengerCurrency: '',
}

export const DriverTravelAllowanceModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    isInland: yup.boolean().defined(),
    vehicleDescription: yup.string().max(50, "Vehicle description must be at most 50 characters.").required("Vehicle description is required."),
    vehicleType: yup.number().transform((value) => Number.isNaN(value) ? undefined : value).required("Vehicle type is required.").min(1, "Vehicle type is required.").typeError("Vehicle type must be a valid number."),
    paymentType: yup.number().transform((value) => Number.isNaN(value) ? undefined : value).required("Payment type is required.").min(1, "Payment type is required.").typeError("Payment type must be a valid number."),
    amount: yup.number().transform((value) => Number.isNaN(value) ? undefined : value).required("Amount is required.").typeError("Amount must be a valid number."),
    currency: yup.string().max(3, "Currency must be at most 3 characters.").required("Currency is required."),
    additionalPassengerAmount: yup.number().transform((value) => Number.isNaN(value) ? undefined : value).nullable().typeError("Additional passenger amount must be a valid number."),
    additionalPassengerCurrency: yup.string().max(3, "Currency must be at most 3 characters.").nullable(),
})